import React, { useState } from 'react';
import { Input, Select, Card, Button, Tag, Spin, Pagination, Row, Col, message } from 'antd';
import {
  SearchOutlined,
  GithubOutlined,
  RocketOutlined,
  ArrowRightOutlined,
  ExperimentOutlined,
  PieChartOutlined,
  SwapOutlined,
  BorderOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
import { MobileFilterBar } from '../../../components/mobile';
import { PublishedStrategy, PaginationMeta, ExecutionResponseDto } from '../../../types';
import apiService from '../../../services/api';
import { ServiceType } from '../../../services/api';
import {
  MarketplaceHeader,
  SearchFilterBar,
  SearchInput,
  FilterSelect,
  StrategiesGrid,
  StrategyCard,
  StrategyCardHeader,
  StrategyIcon,
  StrategyTitle,
  StrategyDescription,
  StrategyFeatures,
  FeatureList,
  FeatureItem,
  StrategyFooter,
  StrategyActions,
  PrimaryButton,
  StatusBadge,
  GithubLink,
  EmptyState,
  LoadingContainer,
  PaginationWrapper
} from '../styles';

const { Option } = Select;

/** One list for both shells, so a new strategy type cannot appear in only one of them. */
const STRATEGY_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'MARKET_MAKER', label: 'Market Maker' },
  { value: 'SMART_BETA', label: 'Smart Beta' },
  { value: 'PAIR_TRADING', label: 'Pair Trading' },
  { value: 'GRID', label: 'Grid Trading' },
];
const coreService = apiService.getService(ServiceType.CORE);

interface StrategyMarketplaceProps {
  strategies: PublishedStrategy[];
  loading: boolean;
  pagination: PaginationMeta;
  searchQuery: string;
  filterType: string;
  onSearch: (value: string) => void;
  onFilterChange: (value: string) => void;
  onPageChange: (page: number, pageSize?: number) => void;
}

const StrategyMarketplace: React.FC<StrategyMarketplaceProps> = ({
  strategies,
  loading,
  pagination,
  searchQuery,
  filterType,
  onSearch,
  onFilterChange,
  onPageChange
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [checkingExecution, setCheckingExecution] = useState<string | null>(null);

  const handleOpen = async (strategy: PublishedStrategy) => {
    setCheckingExecution(strategy.id);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        message.error('Authentication required');
        return;
      }

      // Fetch all executions
      const response = await coreService.get<ExecutionResponseDto[]>('bot/executions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      // Find execution with matching strategyId
      const matchingExecution = response.data?.find(
        (execution) => execution.strategyId === strategy.id
      );

      // Navigate with executionId if found, otherwise just with strategyId
      navigate(getStrategyRoute(strategy.type), {
        state: {
          strategyId: strategy.id,
          strategy: strategy,
          ...(matchingExecution && { executionId: matchingExecution.id, execution: matchingExecution })
        }
      });
    } catch (error: any) {
      console.error('Error checking executions:', error);
      // Navigate anyway with just strategyId if there's an error
      navigate(getStrategyRoute(strategy.type), {
        state: {
          strategyId: strategy.id,
          strategy: strategy
        }
      });
    } finally {
      setCheckingExecution(null);
    }
  };

  const getStrategyRoute = (type: string): string => {
    const routeMap: { [key: string]: string } = {
      'PAIR_TRADING': '/pair-trading',
      'GRID': '/grid-trading',
      'SMART_BETA': '/smart-beta',
    };
    return routeMap[type] || '/coming-soon';
  };

  // Same icon as the strategy's sidebar entry, so a card and its page read as one thing.
  const getStrategyIcon = (type: string) => {
    const iconStyle = { fontSize: '36px', color: '#0ECB81' };
    switch (type) {
      case 'MARKET_MAKER':
        return <ExperimentOutlined style={iconStyle} />;
      case 'SMART_BETA':
        return <PieChartOutlined style={iconStyle} />;
      case 'PAIR_TRADING':
        return <SwapOutlined style={iconStyle} />;
      case 'GRID':
        return <BorderOutlined style={iconStyle} />;
      default:
        return <RocketOutlined style={iconStyle} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'green';
      case 'PENDING':
        return 'orange';
      default:
        return 'default';
    }
  };

  return (
    <div>
      <MarketplaceHeader>
        {isMobile ? (
          <MobileFilterBar
            search={{
              value: searchQuery,
              placeholder: 'Search strategies...',
              onChange: onSearch,
            }}
            groups={[
              {
                key: 'type',
                label: 'Strategy type',
                value: filterType,
                onChange: onFilterChange,
                options: STRATEGY_TYPE_OPTIONS,
              },
            ]}
          />
        ) : (
        <SearchFilterBar>
          <SearchInput
            placeholder="Search strategies..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            allowClear
            size="large"
          />
          <FilterSelect
            value={filterType}
            onChange={(value) => onFilterChange(value as string)}
            size="large"
            style={{ width: 200 }}
          >
            {STRATEGY_TYPE_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </FilterSelect>
        </SearchFilterBar>
        )}
      </MarketplaceHeader>

      {loading ? (
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      ) : strategies.length === 0 ? (
        <EmptyState>
          <p>No strategies found</p>
        </EmptyState>
      ) : (
        <>
          <StrategiesGrid>
            {strategies.map((strategy) => (
              <StrategyCard key={strategy.id}>
                <StrategyCardHeader>
                  <StrategyIcon>{getStrategyIcon(strategy.type)}</StrategyIcon>
                  <div style={{ flex: 1 }}>
                    <StrategyTitle>{strategy.name}</StrategyTitle>
                    <StatusBadge color={getStatusColor(strategy.status)}>
                      {strategy.status}
                    </StatusBadge>
                  </div>
                </StrategyCardHeader>

                <StrategyDescription>{strategy.description}</StrategyDescription>

                {strategy.features && strategy.features.length > 0 && (
                  <StrategyFeatures>
                    <FeatureList>
                      {strategy.features.slice(0, 3).map((feature, index) => (
                        <FeatureItem key={index}>• {feature}</FeatureItem>
                      ))}
                    </FeatureList>
                  </StrategyFeatures>
                )}

                <StrategyFooter>
                  {strategy.plutusUrl && (
                    <GithubLink
                      href={strategy.plutusUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GithubOutlined /> View Source
                    </GithubLink>
                  )}
                  <StrategyActions>
                    <PrimaryButton
                      onClick={() => handleOpen(strategy)}
                      loading={checkingExecution === strategy.id}
                    >
                      Open <ArrowRightOutlined />
                    </PrimaryButton>
                  </StrategyActions>
                </StrategyFooter>
              </StrategyCard>
            ))}
          </StrategiesGrid>

          {pagination.totalPages > 1 && (
            <PaginationWrapper>
              <Pagination
                current={pagination.page}
                total={pagination.total}
                pageSize={pagination.limit}
                onChange={onPageChange}
                showSizeChanger
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} strategies`
                }
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </div>
  );
};

export default StrategyMarketplace;

